// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@mcmc/MobiusOverrides/Tooltip";
import ProductDescription, { ProductDescriptionStyles } from "@mcmc/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@mcmc/Overrides/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@mcmc/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@mcmc/Overrides/Widgets/Cart/CartLineQuantity";
import Caution from "@mcmc/XDL/Icons/Caution";
import Trash from "@mcmc/XDL/Icons/Trash";
import { RobotoCondensed } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import React from "react";
import { HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

interface MiniCartLineProps {
    cart: Cart;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    disabled?: boolean;
}

export interface MiniCartLineStyles {
    container?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    bottomContainer?: InjectableCss;
    price?: ProductPriceStyles;
    extendedPrice?: TypographyProps;
    quantity?: CartLineQuantityStyles;
    errorMessage?: TypographyProps;
    removeLineLink?: LinkPresentationProps;
    errorTooltip?: TooltipPresentationProps;
}

export const rightSectionStyles: InjectableCss = {
    css: css`
        float: right;
        width: calc(100% - 95px);
        display: block;
        overflow-wrap: break-word;
        word-wrap: break-word;
    `,
};

export const miniCartLineStyles: MiniCartLineStyles = {
    container: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            padding: 15px 0;
            &:after {
                content: "";
                clear: both;
                display: table;
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                float: left;
                margin-right: 15px;
            `,
        },
        image: { width: "80px", height: "80px" },
    },
    productDescription: {
        productDetailLink: { ...rightSectionStyles },
        productDetailDescription: { ...rightSectionStyles },
    },
    bottomContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr auto auto auto auto;
            grid-template-areas: "price qty error total delete";
            align-items: center;
            grid-gap: 5px;
            margin-top: 5px;
            float: right;
            min-width: calc(100% - 95px);
        `,
    },
    price: {
        wrapper: {
            css: css`
                grid-area: price;
            `,
        },
        priceWrapper: {
            css: css``,
        },
        price: {
            priceText: {
                ...RobotoCondensed,
                size: 16,
                lineHeight: "18px",
                weight: "normal",
                css: css`
                    white-space: nowrap;
                `,
            },
            unitOfMeasureText: {
                ...RobotoCondensed,
                size: 16,
                lineHeight: "18px",
                weight: "normal",
                css: css`
                    text-transform: uppercase;
                `,
            },
        },
    },
    extendedPrice: {
        ...RobotoCondensed,
        size: 16,
        lineHeight: "18px",
        weight: 700,
        css: css`
            grid-area: total;
            min-width: 55px;
            text-align: right;
        `,
    },

    errorTooltip: {
        iconProps: {
            src: Caution,
            size: 16,
        },
        cssOverrides: {
            tooltipClickable: css`
                grid-area: error;
            `,
        },
    },
    quantity: {
        editableQuantityTextField: {
            cssOverrides: {
                formField: css`
                    width: 45px;
                    grid-area: qty;
                `,
            },
        },
        readOnlyQuantityText: {
            text: {
                ...RobotoCondensed,
                size: 16,
                lineHeight: "18px",
                weight: 700,
                css: css`
                    white-space: nowrap;
                    grid-area: qty;
                `,
            },
        },
    },
    errorMessage: {
        color: "danger",
        size: 14,
        css: css`
            display: block;
            margin-bottom: 5px;
        `,
    },
    removeLineLink: {
        color: "common.ui1",
        hoverStyle: { color: "secondary" },
        icon: {
            iconProps: {
                src: Trash,
                size: 16,
                css: css`
                    margin: 0;
                `,
            },
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            grid-area: delete;
            justify-self: self-end;
        `,
    },
};

const styles = miniCartLineStyles;

type Props = MiniCartLineProps & HasCartLineContext;

const MiniCartLine = ({ cartLine, cart, updateCartLine, removeCartLine, showRemoveAction, disabled }: Props) => {
    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(<Typography {...styles.errorMessage}>{tooManyRequestedMessage}</Typography>);
    }

    let isEditableLine = true;
    if (cartLine.isRestricted) {
        errorMessages.push(<Typography {...styles.errorMessage}>{translate("Restricted product")}</Typography>);
        isEditableLine = false;
    }

    if (!cartLine.isActive) {
        errorMessages.push(<Typography {...styles.errorMessage}>{translate("Inactive product")}</Typography>);
        isEditableLine = false;
    }

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    return (
        <StyledWrapper {...styles.container}>
            <ProductImage
                product={cartLine}
                imageTransformations={transformationsMap.product_thumbnil}
                extendedStyles={styles.productImage}
            />
            <ProductDescription product={cartLine} includeBrandName={true} extendedStyles={styles.productDescription} />
            <StyledWrapper {...styles.bottomContainer}>
                <ProductPrice
                    product={cartLine}
                    showLabel={false}
                    currencySymbol={cart.currencySymbol}
                    showSavings={false}
                    priceNotAvailable={cart.cartNotPriced || !cartLine.pricing}
                    extendedStyles={{ ...styles.price }}
                />
                <CartLineQuantity
                    cart={cart}
                    label=""
                    editable={isEditableLine}
                    disabled={disabled}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={{ ...styles.quantity }}
                />
                {errorMessages.length > 0 && (
                    <Tooltip placement="top" text={errorMessages} {...styles.errorTooltip}></Tooltip>
                )}
                {!cartLine.quoteRequired &&
                    !cart.cartNotPriced &&
                    cartLine.pricing &&
                    (cartLine.pricing.extendedUnitNetPrice > 0 || cartLine.isPromotionItem) && (
                        <Typography {...styles.extendedPrice} data-test-selector="miniCartLine_extendedUnitNetPrice">
                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                        </Typography>
                    )}
                {showRemoveAction && (
                    <Link
                        onClick={removeCartLineClickHandler}
                        data-test-selector="miniCartLine_removeLine"
                        {...styles.removeLineLink}
                        aria-label={translate("Remove Line")}
                    ></Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withCartLine(MiniCartLine);
